// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The whole panel, as geometry ready to draw.
 *
 * Ported from `compute_preview` (`sigmaKi.py:700-793`), including the contract
 * that matters most in an editor: **this never throws.** Problems attach to the
 * object that caused them, so a spec with one bad tab still draws every board,
 * rail and other tab, and the user can see what they are fixing.
 *
 * Build order matches the engine: boards, then rails, then patches, then tabs.
 * Rails derive from what is already placed, so the order is load-bearing rather
 * than incidental.
 */

import { type BBox, bbox, pad } from '../model/bbox.ts';
import type { Point } from '../model/geometry.ts';
import type { Polygon } from './outline.ts';
import { checkClearances, type ClearanceIssue } from './clearance.ts';
import { type Placement, placePolygons, placedBounds, placementFor } from './place.ts';
import { boundsOfPolygons, patchGeometry, railGeometry } from './rails.ts';
import { previewTabs, type TabGeometry } from './tabs.ts';
import { validateSpec, type PanelSpec, type SourceSpec } from './spec.ts';

/** What a caller must supply for one board file: its outlines and source areas. */
export interface SourceProvider {
  /**
   * Outlines and bounding box for a board file and source selector, or null
   * when the file has not been loaded.
   */
  resolve(file: string, source: SourceSpec | undefined): ResolvedSource | null;
}

export interface ResolvedSource {
  outlines: Polygon[];
  bounds: BBox;
  /** Widest Edge.Cuts stroke, which widens the item-selection box. */
  edgeWidth?: number;
}

export interface BoardPreview {
  index: number;
  name: string;
  polys: Polygon[];
  bbox: BBox | null;
  placement: Placement | null;
  /** The file this board came from, so the renderer can find its display list. */
  file: string;
  /**
   * Which part of the source file belongs to this board, in the source's own
   * coordinates. One file often holds many boards, so the renderer needs this to
   * draw the one that was placed rather than all of them.
   */
  selectionBounds: BBox | null;
  error: string | null;
}

export interface RailPreview {
  index: number;
  polys: Polygon[];
  error: string | null;
}

export interface PatchPreview {
  index: number;
  polys: Polygon[];
  error: string | null;
}

export interface TabPreview extends TabGeometry {
  index: number;
}

export interface PanelPreview {
  boards: BoardPreview[];
  rails: RailPreview[];
  patches: PatchPreview[];
  frameCuts: Point[][];
  tabs: TabPreview[];
  manualCuts: Point[][];
  panelBBox: BBox | null;
  /** Spec-level problems: unknown keys, bad units, and so on. */
  errors: string[];
  /** Things that will build, but probably not into what was intended. */
  clearance: ClearanceIssue[];
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function unionBounds(a: BBox | null, b: BBox | null): BBox | null {
  if (a === null) return b;
  if (b === null) return a;
  return bbox(
    Math.min(a.minX, b.minX),
    Math.min(a.minY, b.minY),
    Math.max(a.maxX, b.maxX),
    Math.max(a.maxY, b.maxY),
  );
}

export function computePreview(spec: PanelSpec, sources: SourceProvider): PanelPreview {
  const preview: PanelPreview = {
    boards: [],
    rails: [],
    patches: [],
    frameCuts: [],
    tabs: [],
    manualCuts: [],
    panelBBox: null,
    errors: validateSpec(spec),
    clearance: [],
  };

  /** Every solid placed so far — what rails measure against and tabs bite. */
  const pieces: Polygon[] = [];
  let bounds: BBox | null = null;

  (spec.boards ?? []).forEach((board, index) => {
    const entry: BoardPreview = {
      index,
      name: board?.name ?? `board_${index}`,
      polys: [],
      bbox: null,
      placement: null,
      file: board?.file ?? '',
      selectionBounds: null,
      error: null,
    };
    preview.boards.push(entry);

    try {
      if (typeof board?.file !== 'string' || board.file === '') {
        throw new Error("needs a 'file'");
      }
      const position = board.position;
      if (!Array.isArray(position) || typeof position[0] !== 'number' || typeof position[1] !== 'number') {
        throw new Error("needs 'position: [x, y]'");
      }

      const resolved = sources.resolve(board.file, board.source);
      if (resolved === null) {
        throw new Error(`board file not loaded: ${board.file}`);
      }

      const placement = placementFor(
        resolved.bounds,
        [position[0], position[1]],
        board.anchor ?? 'center',
        board.rotation ?? 0,
      );
      // KiKit takes the items that fit inside the source area widened by the
      // tolerance and the widest edge stroke (`Panel.appendBoard`), so the
      // canvas shows the same selection.
      const tolerance = board.tolerance ?? spec.defaults?.tolerance ?? 1;
      entry.selectionBounds = pad(resolved.bounds, tolerance + (resolved.edgeWidth ?? 0));

      entry.placement = placement;
      entry.polys = placePolygons(placement, resolved.outlines);
      if (entry.polys.length === 0) {
        throw new Error('source area contains no closed outline');
      }
      // The box of the outlines as placed, not of the rotated source area: the
      // engine measures the same way, and rails derive from this.
      entry.bbox = boundsOfPolygons(entry.polys);

      pieces.push(...entry.polys);
      bounds = unionBounds(bounds, entry.bbox);
    } catch (e) {
      entry.error = message(e);
    }
  });

  (spec.rails ?? []).forEach((rail, index) => {
    const entry: RailPreview = { index, polys: [], error: null };
    preview.rails.push(entry);
    try {
      const geometry = railGeometry(rail, bounds, index);
      entry.polys = geometry.polys;
      preview.frameCuts.push(...geometry.cuts);
      pieces.push(...geometry.polys);
      bounds = unionBounds(bounds, boundsOfPolygons(geometry.polys));
    } catch (e) {
      entry.error = message(e);
    }
  });

  (spec.patches ?? []).forEach((patch, index) => {
    const entry: PatchPreview = { index, polys: [], error: null };
    preview.patches.push(entry);
    try {
      entry.polys = patchGeometry(patch, index);
      pieces.push(...entry.polys);
      bounds = unionBounds(bounds, boundsOfPolygons(entry.polys));
    } catch (e) {
      entry.error = message(e);
    }
  });

  // Tabs bite the substrate but do not extend the panel: `panelBBox` is the
  // union of boards, rails and patches only, matching the engine.
  const tabs = spec.tabs ?? [];
  if (tabs.length > 0 && pieces.length === 0) {
    tabs.forEach((_, index) => {
      preview.tabs.push({ index, polys: [], cuts: [], error: 'no substrate to attach to' });
    });
  } else {
    previewTabs(pieces, tabs, spec.defaults ?? {}).forEach((tab, index) => {
      preview.tabs.push({ index, ...tab });
    });
  }

  for (const cut of spec.manual_cuts ?? []) {
    if (Array.isArray(cut) && cut.length >= 2) {
      preview.manualCuts.push(cut.map(([x, y]) => [x, y] as Point));
    }
  }

  preview.clearance = checkClearances({
    rails: preview.rails.filter((r) => r.error === null),
    boards: preview.boards
      .filter((b) => b.error === null)
      .map((b) => ({ index: b.index, name: b.name, polys: b.polys })),
  });

  preview.panelBBox = bounds;
  return preview;
}

/** Every cut line the panel will carry, for drawing. */
export function allCuts(preview: PanelPreview): Point[][] {
  return [
    ...preview.frameCuts,
    ...preview.tabs.flatMap((t) => t.cuts),
    ...preview.manualCuts,
  ];
}

/** Warnings: the panel will build, but probably not as intended. */
export function previewWarnings(preview: PanelPreview): string[] {
  return preview.clearance.map((issue) => issue.message);
}

/** Per-object problems, phrased for the console. */
export function previewProblems(preview: PanelPreview): string[] {
  const out = [...preview.errors];
  for (const b of preview.boards) if (b.error) out.push(`boards[${b.index}]: ${b.error}`);
  for (const r of preview.rails) if (r.error) out.push(`rails[${r.index}]: ${r.error}`);
  for (const p of preview.patches) if (p.error) out.push(`patches[${p.index}]: ${p.error}`);
  for (const t of preview.tabs) if (t.error) out.push(`tabs[${t.index}]: ${t.error}`);
  return out;
}
